import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUpload, FiCheck, FiAlertTriangle, FiImage, FiSettings } = FiIcons;

const LogoUploader = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [optimizedPreview, setOptimizedPreview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationStats, setOptimizationStats] = useState(null);

  // Optimization settings
  const MAX_WIDTH = 512;
  const MAX_HEIGHT = 512;
  const MAX_FILE_SIZE = 100 * 1024; // 100KB target for optimized logo
  const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];

  const optimizeImage = async (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          if (aspectRatio > 1) {
            // Landscape
            width = Math.min(width, MAX_WIDTH);
            height = width / aspectRatio;
          } else {
            // Portrait or square
            height = Math.min(height, MAX_HEIGHT);
            width = height * aspectRatio;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw image with high quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels to achieve target file size
        const tryQuality = (qualityIndex = 0) => {
          if (qualityIndex >= QUALITY_STEPS.length) {
            // If we can't achieve target size, use the last attempt
            canvas.toBlob(resolve, 'image/jpeg', QUALITY_STEPS[QUALITY_STEPS.length - 1]);
            return;
          }

          const quality = QUALITY_STEPS[qualityIndex];
          canvas.toBlob((blob) => {
            if (blob.size <= MAX_FILE_SIZE || qualityIndex === QUALITY_STEPS.length - 1) {
              resolve(blob);
            } else {
              tryQuality(qualityIndex + 1);
            }
          }, 'image/jpeg', quality);
        };

        // Start with PNG if original was PNG and size is reasonable
        if (file.type === 'image/png' && file.size <= MAX_FILE_SIZE * 2) {
          canvas.toBlob((blob) => {
            if (blob.size <= MAX_FILE_SIZE) {
              resolve(blob);
            } else {
              tryQuality(0);
            }
          }, 'image/png');
        } else {
          tryQuality(0);
        }
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.match(/^image\/(png|jpeg|jpg)$/)) {
      setError('Please select a PNG or JPG image file');
      return;
    }

    // Validate file size (max 10MB for upload)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setOptimizing(true);

    // Create original preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(selectedFile);

    try {
      // Optimize the image
      const optimizedBlob = await optimizeImage(selectedFile);
      
      // Create optimized preview
      const optimizedReader = new FileReader();
      optimizedReader.onload = (e) => {
        setOptimizedPreview(e.target.result);
        
        // Set optimization statistics
        setOptimizationStats({
          originalSize: selectedFile.size,
          optimizedSize: optimizedBlob.size,
          compressionRatio: ((selectedFile.size - optimizedBlob.size) / selectedFile.size * 100).toFixed(1),
          dimensions: `${MAX_WIDTH}×${MAX_HEIGHT} max`
        });
        
        setOptimizing(false);
      };
      optimizedReader.readAsDataURL(optimizedBlob);

      // Store optimized blob for upload
      setFile(optimizedBlob);
    } catch (err) {
      setError('Failed to optimize image: ' + err.message);
      setOptimizing(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;
          
          // Store in localStorage for persistence
          localStorage.setItem('brioche-custom-logo', base64Data);
          
          setSuccess(true);
          
          setTimeout(() => {
            onSuccess();
            onClose();
            // Refresh the page to load the new logo
            window.location.reload();
          }, 2000);
        } catch (err) {
          setError('Failed to process logo file: ' + err.message);
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Upload failed: ' + err.message);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setOptimizedPreview(null);
    setError(null);
    setSuccess(false);
    setOptimizing(false);
    setOptimizationStats(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-brand-card rounded-card p-6 w-full max-w-2xl mx-4 shadow-card max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-brand-fg">Upload & Optimize Logo</h3>
              <motion.button
                onClick={onClose}
                className="p-2 hover:bg-brand-muted2/20 rounded-button transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-brand-muted" />
              </motion.button>
            </div>

            {success ? (
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <SafeIcon icon={FiCheck} className="w-16 h-16 text-brand-good mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-brand-fg mb-2">Logo Updated!</h4>
                <p className="text-brand-muted">Your logo has been optimized and uploaded successfully. Refreshing dashboard...</p>
              </motion.div>
            ) : (
              <>
                {/* Optimization Info */}
                <div className="mb-6 p-4 bg-brand-accent/10 rounded-lg border border-brand-accent/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiSettings} className="w-5 h-5 text-brand-accent" />
                    <h4 className="text-brand-accent font-medium">Automatic Optimization</h4>
                  </div>
                  <ul className="text-brand-muted text-sm space-y-1">
                    <li>• Resizes to maximum {MAX_WIDTH}×{MAX_HEIGHT} pixels</li>
                    <li>• Compresses to under {formatFileSize(MAX_FILE_SIZE)} for fast loading</li>
                    <li>• Maintains aspect ratio and quality</li>
                    <li>• Supports PNG and JPG formats</li>
                  </ul>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-brand-fg text-sm font-medium mb-3">
                    Select Logo File
                  </label>
                  <div className="border-2 border-dashed border-brand-muted2/40 rounded-lg p-8 text-center hover:border-brand-accent/50 transition-colors">
                    <SafeIcon icon={FiImage} className="w-12 h-12 text-brand-muted mx-auto mb-4" />
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleFileChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="cursor-pointer text-brand-fg hover:text-brand-accent transition-colors"
                    >
                      Click to upload logo
                    </label>
                    <p className="text-brand-muted text-sm mt-2">
                      PNG or JPG files (up to 10MB) - will be automatically optimized
                    </p>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <motion.div
                    className="mb-6 p-4 bg-brand-bad/10 border border-brand-bad/20 rounded-lg"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-brand-bad" />
                      <span className="text-brand-bad font-medium">Error</span>
                    </div>
                    <p className="text-brand-bad text-sm mt-1">{error}</p>
                  </motion.div>
                )}

                {/* Optimization Progress */}
                {optimizing && (
                  <motion.div
                    className="mb-6 p-4 bg-brand-muted2/10 rounded-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
                      <div>
                        <p className="text-brand-fg font-medium">Optimizing Image...</p>
                        <p className="text-brand-muted text-sm">Resizing and compressing for best performance</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Preview Comparison */}
                {preview && optimizedPreview && optimizationStats && (
                  <motion.div
                    className="mb-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h4 className="text-brand-fg font-medium mb-4">Optimization Results</h4>
                    
                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-brand-muted2/10 rounded-lg">
                      <div className="text-center">
                        <p className="text-brand-muted text-xs uppercase tracking-wide">Original Size</p>
                        <p className="text-brand-fg font-semibold">{formatFileSize(optimizationStats.originalSize)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-brand-muted text-xs uppercase tracking-wide">Optimized Size</p>
                        <p className="text-brand-good font-semibold">{formatFileSize(optimizationStats.optimizedSize)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-brand-muted text-xs uppercase tracking-wide">Size Reduction</p>
                        <p className="text-brand-accent font-semibold">{optimizationStats.compressionRatio}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-brand-muted text-xs uppercase tracking-wide">Max Dimensions</p>
                        <p className="text-brand-muted font-semibold">{optimizationStats.dimensions}</p>
                      </div>
                    </div>

                    {/* Visual Comparison */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-brand-muted2/10 rounded-lg p-4 text-center">
                        <h5 className="text-brand-muted text-sm font-medium mb-3">Original</h5>
                        <img
                          src={preview}
                          alt="Original logo"
                          className="w-24 h-24 object-contain mx-auto rounded-lg"
                        />
                        <p className="text-brand-muted text-xs mt-2">
                          {formatFileSize(optimizationStats.originalSize)}
                        </p>
                      </div>
                      
                      <div className="bg-brand-good/10 rounded-lg p-4 text-center border border-brand-good/20">
                        <h5 className="text-brand-good text-sm font-medium mb-3">Optimized (Preview)</h5>
                        <img
                          src={optimizedPreview}
                          alt="Optimized logo preview"
                          className="w-24 h-24 object-contain mx-auto rounded-lg"
                        />
                        <p className="text-brand-good text-xs mt-2">
                          {formatFileSize(optimizationStats.optimizedSize)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                  <motion.button
                    onClick={() => {
                      resetForm();
                      onClose();
                    }}
                    className="px-4 py-2 bg-brand-muted2/20 hover:bg-brand-muted2/30 text-brand-fg rounded-button transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  
                  <motion.button
                    onClick={handleUpload}
                    disabled={!file || uploading || optimizing || !optimizedPreview}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-button text-sm transition-colors ${
                      !file || uploading || optimizing || !optimizedPreview
                        ? 'bg-brand-muted2/20 text-brand-muted cursor-not-allowed'
                        : 'bg-brand-accent hover:bg-brand-accent/80 text-brand-bg'
                    }`}
                    whileHover={!file || uploading || optimizing ? {} : { scale: 1.02 }}
                    whileTap={!file || uploading || optimizing ? {} : { scale: 0.98 }}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-brand-muted border-t-transparent rounded-full animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : optimizing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-brand-muted border-t-transparent rounded-full animate-spin" />
                        <span>Optimizing...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiUpload} className="w-4 h-4" />
                        <span>Upload Optimized Logo</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoUploader;